import * as React from "react";
import { tv } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "./AiDisclaimer";
import useEmblaCarousel from "embla-carousel-react";
import { SectionTitleProps, SectionTitle } from "./SectionTitle";
import { ChevronIcon } from "./ChevronIcon";
import {
  ImageProps,
  SectionBackgroundColorType,
  SectionTopPaddingSizeType,
  SectionBottomPaddingSizeType,
  BackgroundOverlayOptionType,
  BackgroundOverlayColorType,
} from "./types";
import { sectionWrapper } from "./sectionWrappers";

const heroContainer = tv({
  base: cn(
    "uipm:relative uipm:grid uipm:min-h-[400px]",
    "uipm:py-[var(--container-card-large-padding)] uipm:px-[var(--container-margin)]",
    "uipm:grid-cols-1 uipm:grid-rows-[1fr_auto] uipm:gap-[var(--container-title-gap)]",
    "uipm:md:p-[var(--container-card-large-padding)]",
    "uipm:md:grid-rows-1 uipm:md:gap-0 uipm:md:items-center",
  ),
  variants: {
    showNavigation: {
      true: "uipm:md:grid-cols-[48px_1fr_48px]",
      false: "uipm:md:grid-cols-1",
    },
  },
});

const backgroundOverlayStyle = tv({
  base: "uipm:absolute uipm:inset-0",
  variants: {
    overlayColor: {
      primary: "uipm:bg-[var(--surface-surface-accent)]",
      secondary: "uipm:bg-[var(--surface-surface-accent-secondary)]",
      "accent-light": "uipm:bg-[var(--surface-surface-accent-light)]",
    },
    overlayOpacity: {
      "brand-10": "uipm:opacity-10",
      "brand-20": "uipm:opacity-20",
      "brand-30": "uipm:opacity-30",
      "brand-40": "uipm:opacity-40",
      "brand-50": "uipm:opacity-50",
      "brand-60": "uipm:opacity-60",
      "brand-70": "uipm:opacity-70",
      "brand-80": "uipm:opacity-80",
      "brand-90": "uipm:opacity-90",
      "brand-full": "uipm:opacity-100",
      "brand-empty": "uipm:opacity-0",
    },
  },
  defaultVariants: {
    overlayColor: "primary",
    overlayOpacity: "brand-50",
  },
});

const navigationButton = tv({
  base: cn(
    "uipm:flex uipm:items-center uipm:justify-center uipm:cursor-pointer",
    "uipm:w-[48px] uipm:h-[48px] uipm:p-3 uipm:rounded-full",
    "uipm:bg-[var(--surface-surface-secondary)] uipm:text-[var(--typography-colors-text-inverted)]",
    "uipm:transition-opacity uipm:duration-200",
    "disabled:uipm:opacity-50 disabled:uipm:cursor-not-allowed",
    "uipm:hover:opacity-80",
  ),
});

export interface HeroCarouselSlide {
  sectionTitle?: SectionTitleProps;
}

export interface HeroCarouselProps
  extends Omit<ComponentProps<"section">, "title"> {
  image?: ImageProps;
  backgroundOverlay?: BackgroundOverlayOptionType;
  backgroundOverlayColor?: BackgroundOverlayColorType;
  backgroundColor?: SectionBackgroundColorType;
  topPadding?: SectionTopPaddingSizeType;
  bottomPadding?: SectionBottomPaddingSizeType;
  slides: HeroCarouselSlide[];
}

// Slide content component to avoid duplication
const SlideContent: React.FC<{ slide: HeroCarouselSlide }> = ({ slide }) => (
  <div
    className={cn(
      "uipm:flex uipm:flex-col uipm:gap-[var(--margin-margin-default)]",
      "uipm:md:px-[var(--container-card-padding)] uipm:md:flex-1 uipm:md:max-w-[790px] uipm:md:mx-auto",
      "uipm:lg:px-[var(--padding-padding-m)]",
    )}
  >
    {/* SectionTitle */}
    {slide.sectionTitle && (
      <SectionTitle
        {...slide.sectionTitle}
        className={cn(
          "uipm:font-normal uipm:text-[var(--typography-colors-text-inverted)]",
          slide.sectionTitle.className,
        )}
      />
    )}
  </div>
);

export const HeroCarousel: React.FC<HeroCarouselProps> = ({
  image,
  backgroundOverlay = "brand-50",
  backgroundOverlayColor = "primary",
  backgroundColor = "none",
  topPadding = "none",
  bottomPadding = "none",
  slides,
  className,
  id,
  ...props
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
  });
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  React.useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  if (!slides || slides.length === 0) return null;

  // Hide navigation buttons if there's only one slide or less
  const showNavigation = slides.length > 1;

  return (
    <section
      id={id}
      className={sectionWrapper({ backgroundColor, topPadding, bottomPadding })}
      {...props}
    >
      <div className="uipm:max-w-[1440px] uipm:mx-auto">
        <div
          className={cn(
            "uipm:relative uipm:font-normal uipm:overflow-hidden",
            "uipm:md:rounded-[20px]",
            className,
          )}
        >
          {/* Background Image */}
          {image && (
            <div className="uipm:absolute uipm:inset-0">
              <Image
                src={image.src}
                alt={parseAiAlt(image.alt).cleanAlt}
                fill
                className="uipm:object-cover"
                sizes="100vw"
                fetchPriority="high"
                priority
              />
              <AiDisclaimerForAlt alt={image.alt} src={image.src} />
            </div>
          )}

          {/* Background Overlay */}
          <div
            className={backgroundOverlayStyle({
              overlayColor: backgroundOverlayColor,
              overlayOpacity: backgroundOverlay,
            })}
          />

          {/* Main Container - Grid Layout */}
          <div className={heroContainer({ showNavigation })}>
            {/* Left Navigation Button (Hidden on Mobile and when only one slide) */}
            {showNavigation && (
              <button
                className={cn(navigationButton(), "uipm:hidden uipm:md:flex")}
                onClick={scrollPrev}
                disabled={!canScrollPrev}
              >
                <ChevronIcon
                  className="uipm:w-6 uipm:h-6 uipm:rotate-180 uipm:bg-[var(--surface-surface-secondary)]"
                  color="var(--color-color-accent)"
                />
              </button>
            )}

            {/* Carousel Container */}
            <div className="uipm:overflow-hidden uipm:flex-1" ref={emblaRef}>
              <div className="uipm:flex">
                {slides.map((slide, index) => (
                  <div
                    key={index}
                    className={cn(
                      "uipm:flex-[0_0_100%] uipm:min-w-0 uipm:relative uipm:z-10",
                      "uipm:md:flex uipm:md:items-center uipm:md:justify-between",
                    )}
                  >
                    <SlideContent slide={slide} />
                  </div>
                ))}
              </div>
            </div>

            {/* Right Navigation Button (Hidden on Mobile and when only one slide) */}
            {showNavigation && (
              <button
                className={cn(navigationButton(), "uipm:hidden uipm:md:flex")}
                onClick={scrollNext}
                disabled={!canScrollNext}
              >
                <ChevronIcon
                  className="uipm:w-6 uipm:h-6 uipm:bg-[var(--surface-surface-secondary)]"
                  color="var(--color-color-accent)"
                />
              </button>
            )}

            {/* Mobile Navigation (Only visible on mobile and when more than one slide) */}
            {showNavigation && (
              <div
                className={cn(
                  "uipm:relative uipm:z-10 uipm:flex uipm:flex-col uipm:items-center uipm:gap-[var(--padding-padding-l)]",
                  "uipm:md:hidden",
                )}
              >
                <div className="uipm:flex uipm:gap-[var(--container-gutter)] uipm:justify-center">
                  <button
                    className={navigationButton()}
                    onClick={scrollPrev}
                    disabled={!canScrollPrev}
                  >
                    <ChevronIcon
                      className="uipm:w-6 uipm:h-6 uipm:rotate-180 uipm:bg-[var(--surface-surface-secondary)]"
                      color="var(--color-color-accent)"
                    />
                  </button>
                  <button
                    className={navigationButton()}
                    onClick={scrollNext}
                    disabled={!canScrollNext}
                  >
                    <ChevronIcon
                      className="uipm:w-6 uipm:h-6 uipm:bg-[var(--surface-surface-secondary)]"
                      color="var(--color-color-accent)"
                    />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
